'use client'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Spinner,
  Textarea,
} from '@repo/ui'
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  ShieldCheck,
  Sparkles,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  type CompatibilityFinding,
  type CompatibilityResult,
  type CompatibilitySeverity,
  useFixPromptCompatibility,
  usePromptCompatibility,
} from '@/hooks/use-agent-optimizer'

const SEVERITY_STYLES: Record<
  CompatibilitySeverity,
  { icon: React.ElementType; className: string; label: string }
> = {
  error: {
    icon: XCircle,
    className: 'border-destructive/40 bg-destructive/5 text-destructive',
    label: 'Erro',
  },
  warning: {
    icon: AlertTriangle,
    className: 'border-warning/40 bg-warning/5 text-warning',
    label: 'Aviso',
  },
  info: {
    icon: Info,
    className: 'border-border bg-muted/40 text-muted-foreground',
    label: 'Info',
  },
}

function FindingCard({ finding }: { finding: CompatibilityFinding }) {
  const style = SEVERITY_STYLES[finding.severity] ?? SEVERITY_STYLES.warning
  const Icon = style.icon
  return (
    <div className={`rounded-md border p-3 ${style.className}`}>
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 size-4 shrink-0" />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-foreground text-sm font-medium">
              {finding.title}
            </p>
            <span className="text-muted-foreground text-2xs rounded border px-1.5 py-0.5 font-medium">
              {finding.fixableByPrompt
                ? 'Corrigível no prompt'
                : 'Exige configuração'}
            </span>
          </div>
          <p className="text-muted-foreground mt-0.5 text-xs">
            {finding.detail}
          </p>
          {finding.suggestion ? (
            <p className="text-foreground/80 mt-1 text-xs">
              <span className="font-medium">Sugestão:</span>{' '}
              {finding.suggestion}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}

interface PromptCompatibilityCheckProps {
  orgId: string | null
  agentId: string | null
  instructions: string
  onApply: (value: string) => void
}

export function PromptCompatibilityCheck({
  orgId,
  agentId,
  instructions,
  onApply,
}: PromptCompatibilityCheckProps) {
  const check = usePromptCompatibility(orgId, agentId)
  const fix = useFixPromptCompatibility(orgId, agentId)
  const [result, setResult] = useState<CompatibilityResult | null>(null)
  const [fixOpen, setFixOpen] = useState(false)
  const [draftPrompt, setDraftPrompt] = useState('')
  const [changes, setChanges] = useState<string[]>([])

  async function run() {
    try {
      const data = await check.mutateAsync({ instructions })
      setResult(data)
    } catch {
      toast.error('Não foi possível analisar o prompt agora.')
    }
  }

  async function runFix() {
    const fixableFindings =
      result?.findings.filter((finding) => finding.fixableByPrompt) ?? []
    try {
      const data = await fix.mutateAsync({
        instructions,
        findings: fixableFindings,
      })
      setDraftPrompt(data.correctedPrompt)
      setChanges(data.changes)
      setFixOpen(true)
    } catch {
      // O interceptor da API exibe a causa retornada pelo servidor.
    }
  }

  function applyFix() {
    onApply(draftPrompt)
    setFixOpen(false)
    setResult(null)
    toast.success('Correções de prompt aplicadas.')
  }

  return (
    <div className="mt-3 rounded-md border border-dashed p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-muted-foreground size-4" />
          <p className="text-sm font-medium">
            Compatibilidade do prompt com o sistema
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void run()}
          disabled={check.isPending || !orgId || !agentId}
        >
          {check.isPending ? <Spinner /> : <ShieldCheck className="size-4" />}
          Verificar compatibilidade
        </Button>
      </div>
      <p className="text-muted-foreground mt-1 text-xs">
        Checagem objetiva entre as instruções e a configuração do agente: campos
        obrigatórios, agenda, ações e conhecimento.
      </p>

      {result ? (
        result.findings.length === 0 ? (
          <div className="border-success/40 bg-success/5 text-success mt-3 flex items-center gap-2 rounded-md border p-3 text-sm">
            <CheckCircle2 className="size-4" />
            Nenhuma incompatibilidade encontrada.
          </div>
        ) : (
          <div className="mt-3 flex flex-col gap-2">
            {result.findings.map((finding) => (
              <FindingCard key={finding.code} finding={finding} />
            ))}
            <div className="flex justify-end pt-1">
              <Button
                type="button"
                size="sm"
                onClick={() => void runFix()}
                disabled={
                  fix.isPending ||
                  !result.findings.some((finding) => finding.fixableByPrompt)
                }
              >
                {fix.isPending ? <Spinner /> : <Sparkles className="size-4" />}
                Corrigir incompatibilidades
              </Button>
            </div>
            {!result.findings.some((finding) => finding.fixableByPrompt) && (
              <p className="text-muted-foreground text-right text-xs">
                As pendências encontradas exigem alteração nas configurações,
                não no prompt.
              </p>
            )}
          </div>
        )
      ) : null}

      <Dialog open={fixOpen} onOpenChange={setFixOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Corrigir incompatibilidades</DialogTitle>
            <DialogDescription>
              Revise o prompt corrigido (editável) antes de aplicar. Nada é
              salvo até você clicar em Aplicar.
            </DialogDescription>
          </DialogHeader>

          {changes.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <Label className="text-muted-foreground text-xs font-medium">
                O que mudou
              </Label>
              <ul className="text-foreground/80 list-disc space-y-1 pl-5 text-xs">
                {changes.map((change, i) => (
                  <li key={i}>{change}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label className="text-muted-foreground text-xs font-medium">
              Prompt corrigido
            </Label>
            <Textarea
              value={draftPrompt}
              onChange={(e) => setDraftPrompt(e.target.value)}
              rows={16}
              className="resize-none text-xs leading-relaxed"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setFixOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={applyFix}
              disabled={!draftPrompt.trim()}
            >
              Aplicar correções
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
